import { Dropdown, IconButton, Icon, iconWeight } from '@nula/core-ui';
import { ConfirmMenuItem } from '@/components/confirm-menu-item/confirm-menu-item';
import { AddToCollectionSubmenu } from './add-to-collection-submenu';

interface RecentsItemMenuProps {
  /** Whether each action is available for this entity (kind × permission).
   *  Composed by the caller from the capability matrix. */
  showAddToCollection: boolean;
  showRename: boolean;
  showArchive: boolean;
  showDelete: boolean;
  showRemoveSelf: boolean;
  onOpen: () => void;
  /** Add the entity to the chosen collection (from the searchable submenu). */
  onAddToCollection: (collectionId: string) => void;
  onRename: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onRemoveSelf: () => void;
}

type Item = {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  danger?: boolean;
  type?: 'divider';
  onClick?: () => void;
  submenu?: React.ReactNode;
};

/**
 * The hover-revealed "⋯" overflow menu for a Recents sidebar row. Which items
 * appear is driven by explicit availability flags (the caller applies the
 * per-kind + permission capability matrix). Delete uses the two-step in-place
 * ConfirmMenuItem (PRD-23).
 */
export function RecentsItemMenu({
  showAddToCollection, showRename, showArchive, showDelete, showRemoveSelf,
  onOpen, onAddToCollection, onRename, onArchive, onDelete, onRemoveSelf,
}: RecentsItemMenuProps) {
  const ic = (name: string) => <Icon name={name} size={14} weight={iconWeight.light} />;

  const items: Item[] = [
    { key: 'open', label: 'Open', icon: ic('open_in_new'), onClick: onOpen },
  ];

  if (showAddToCollection) {
    items.push({
      key: 'collection',
      label: 'Add to collection',
      icon: ic('folder_open'),
      submenu: <AddToCollectionSubmenu onAdd={onAddToCollection} />,
    });
  }
  if (showRename) items.push({ key: 'rename', label: 'Rename', icon: ic('edit'), onClick: onRename });
  if (showArchive) items.push({ key: 'archive', label: 'Archive', icon: ic('archive'), onClick: onArchive });

  if (showDelete) {
    if (items.length > 1) items.push({ key: 'div', label: '', type: 'divider' });
    items.push({
      key: 'delete',
      icon: ic('delete'),
      danger: true,
      // No-op onClick — the two-step logic lives in ConfirmMenuItem, which
      // stops propagation so the Dropdown stays open through the confirm.
      onClick: () => {},
      label: <ConfirmMenuItem label="Delete" confirmLabel="Click to confirm" onConfirm={onDelete} />,
    });
  }

  if (showRemoveSelf) {
    if (items.length > 1) items.push({ key: 'div2', label: '', type: 'divider' });
    items.push({
      key: 'remove-self',
      icon: ic('logout'),
      onClick: () => {},
      label: <ConfirmMenuItem label="Remove from my workspace" confirmLabel="Click to confirm" onConfirm={onRemoveSelf} />,
    });
  }

  return (
    <Dropdown trigger={['click']} placement="bottom-end" menu={{ items }}>
      <IconButton size="sm" aria-label="More options">
        <Icon name="more_horiz" size={16} weight={iconWeight.light} />
      </IconButton>
    </Dropdown>
  );
}
